import React, { useState, useMemo, useEffect, useCallback } from "react";
import CardBase from "@/components/cards/CardBase";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/InputDefault";
import IconProvider from "@/components/ui/IconProvider";
import {
  DataSourceType,
  getColumnsForDataSource,
  getDataForDataSource,
  TableColumn,
  DATA_SOURCES,
} from "@/lib/dataSources";
import { useDataSource } from "@/lib/useDataSource";
import ExpandableTableContent from "./ExpandableStringsTableContent";
import TranslationWarningDialog from "./TranslationWarningDialog";

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
export interface TableRow {
  [key: string]: React.ReactNode;
}

interface TableWidgetProps {
  columns?: TableColumn[];
  data?: TableRow[];
  dataSource: DataSourceType;
  showTitle: boolean;
  columnsConfig?: { key: string; label: string; field: string }[];
  selectedColumns?: string[]; // New prop for column selection
  columnLabels?: Record<string, string>; // New prop for custom column labels
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;
  title?: string;
  showSearch?: boolean;
  showFilters?: boolean;
  requiredTags?: string[];
  backgroundColor?: string;
  shadowType?: "black" | "gradient" | "none" | "color";
  shadowColor?: string;
  /**
   * Optional: If provided, TableWidget will add an 'Actions' column and render an edit button for each row.
   * The callback receives the row data as its argument.
   */
  onEdit?: (row: TableRow) => void;
  /**
   * Optional: If provided, TableWidget will render a duplicate button for each row.
   * The callback receives the row data as its argument.
   */
  onDuplicate?: (row: TableRow) => void;
  /**
   * Optional: If provided, TableWidget will render a delete button for each row.
   * The callback receives the row data as its argument.
   */
  onDelete?: (row: TableRow) => void;
  /**
   * Optional: If provided, TableWidget will show an "Add New" button in the header.
   * The callback is called when the button is clicked.
   */
  onAddNew?: () => void;
  /**
   * Optional: Custom text for the "Add New" button. If not provided, will use "Add New {dataSourceName}".
   */
  addNewButtonText?: string;
  /**
   * Optional: Whether to show the "Add New" button. If not provided, will show if onAddNew is provided.
   */
  showAddNew?: boolean;
  /**
   * Optional: Callback for when a cell value is updated via inline editing.
   * Receives the row data, column key, and new value.
   */
  onCellUpdate?: (row: TableRow, columnKey: string, newValue: string) => void;
}

const TableWidget: React.FC<TableWidgetProps> = ({
  columns,
  data,
  dataSource,
  columnsConfig,
  selectedColumns,
  columnLabels,
  page: externalPage = 1,
  pageSize = 10,
  total = 30,
  onPageChange,
  showTitle = true,
  title = "Table",
  showSearch = true,
  showFilters = true,
  requiredTags = [],
  onEdit,
  onDuplicate,
  onDelete,
  onAddNew,
  addNewButtonText,
  showAddNew,
  backgroundColor = "#ffffff",
  shadowType = "black",
  shadowColor = "#000000",
  onCellUpdate,
}) => {
  
  // Internal pagination state for when no external onPageChange is provided
  const [internalPage, setInternalPage] = useState(1);

  // Inline editing state
  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    columnKey: string;
    value: string;
  } | null>(null);

  // Expandable rows state - use row IDs for persistence across re-renders
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Translation warning dialog state
  const [translationWarning, setTranslationWarning] = useState<{
    open: boolean;
    rowIndex: number;
    columnKey: string;
    newValue: string;
    translationCount: number;
  } | null>(null);

  // Use external page if onPageChange is provided, otherwise use internal state
  const currentPage = onPageChange ? externalPage : internalPage;
  // Handle page changes
  const handlePageChange = useCallback(
    (newPage: number) => {
      if (onPageChange) {
        onPageChange(newPage);
      } else {
        setInternalPage(newPage);
      }
    },
    [onPageChange]
  );
  

  // Reset internal page to 1 when data source changes (for internal pagination)
  useEffect(() => {
    if (!onPageChange) {
      setInternalPage(1);
    }
  }, [dataSource, onPageChange]);



  const ds = useDataSource(dataSource);
  let isLoading = false;
  let finalColumns = columns;
  let finalData = data;
  isLoading = ds.isLoading;

  // Check if the data source supports expandable rows
  const isExpandable = DATA_SOURCES[dataSource]?.expandable || false;
  // Check if we have data from the data source
  const hasDataSourceData = ds.data && ds.data.length > 0;

  // If no columns are provided, use the automatic column mapping from dataSources
  if (!finalColumns && !columnsConfig) {
    // Use tableData for strings to get dynamic columns, otherwise use the static columns
    let allColumns;
    if (dataSource === "strings" && ds.tableData) {
      allColumns = ds.tableData.columns;
    } else {
      allColumns = getColumnsForDataSource(dataSource);
    }

    // Filter columns based on selectedColumns if provided
    if (selectedColumns && selectedColumns.length > 0) {
      finalColumns = allColumns.filter((col) =>
        selectedColumns.includes(col.key)
      );
      // Reorder columns according to selectedColumns order
      finalColumns.sort((a, b) => {
        const aIndex = selectedColumns.indexOf(a.key);
        const bIndex = selectedColumns.indexOf(b.key);
        return aIndex - bIndex;
      });

      // Apply custom labels if provided
      if (columnLabels) {
        finalColumns = finalColumns.map((col) => ({
          ...col,
          label: columnLabels[col.key] || col.label,
        }));
      }
    } else {
      finalColumns = allColumns;
    }
  }

  // If no data is provided but we have data from the data source, transform it
  if (!finalData && hasDataSourceData) {
    // Use tableData for strings to get properly formatted data, otherwise use the old method
    if (dataSource === "strings" && ds.tableData) {
      finalData = ds.tableData.data.map((item) => {
        const row: Record<string, React.ReactNode> = {};
        // Always include all columns to preserve full data for handlers
        Object.entries(item).forEach(([key, value]) => {
          row[key] = value as React.ReactNode;
        });
        return row;
      });
    } else {
      finalData = getDataForDataSource(dataSource, ds.data).map((item) => {
        const row: Record<string, React.ReactNode> = {};
        // Always include all columns to preserve full data for handlers
        Object.entries(item).forEach(([key, value]) => {
          row[key] = value as React.ReactNode;
        });
        return row;
      });
    }
  }
  const { t } = usePlatformTranslation();

  // Ensure "actions" column is always last
  const reorderColumns = (columns: TableColumn[] = []) => {
    const actionsCol = columns.find((col) => col.key === "actions");
    const otherCols = columns.filter((col) => col.key !== "actions");
    return actionsCol ? [...otherCols, actionsCol] : columns;
  };

  finalColumns = reorderColumns(finalColumns);

  // Inject Actions column if any action handlers are provided
  const hasActions = !!(onEdit || onDuplicate || onDelete);
  let columnsWithActions = finalColumns || [];
  if (hasActions) {
    // Remove any existing 'actions' column to avoid duplicates
    columnsWithActions = columnsWithActions.filter(
      (col) => col.key !== "actions"
    );
    columnsWithActions = [
      ...columnsWithActions,
      { key: "actions", label: "Actions", align: "center" },
    ];
  }

  const [searchTerm, setSearchTerm] = useState("");
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {}
  );

  // Filter data based on search term and column filters
  const filteredData = useMemo(() => {
    const filtered = (finalData || []).filter((row) => {
      // Hard tag filter
      if (requiredTags && requiredTags.length > 0) {
        const rowTags = row.tags as string[] | undefined;
        if (!rowTags || !rowTags.some((tag) => requiredTags.includes(tag))) {
          return false;
        }
      }
      // Global search
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const hasMatch = (finalColumns || []).some((col) => {
          const value = row[col.key];
          if (value === null || value === undefined) return false;

          // Handle different value types
          if (Array.isArray(value)) {
            return value.some((item) =>
              String(item).toLowerCase().includes(searchLower)
            );
          } else if (React.isValidElement(value)) {
            // Skip React elements for text search
            return false;
          } else {
            return String(value).toLowerCase().includes(searchLower);
          }
        });
        if (!hasMatch) return false;
      }

      // Column-specific filters
      for (const [columnKey, filterValue] of Object.entries(columnFilters)) {
        if (filterValue) {
          const value = row[columnKey];
          if (value === null || value === undefined) return false;

          const filterLower = filterValue.toLowerCase();
          if (Array.isArray(value)) {
            const hasMatch = value.some((item) =>
              String(item).toLowerCase().includes(filterLower)
            );
            if (!hasMatch) return false;
          } else if (React.isValidElement(value)) {
            // Skip React elements for filtering
            return false;
          } else {
            if (!String(value).toLowerCase().includes(filterLower)) {
              return false;
            }
          }
        }
      }

      return true;
    });

    // Sort alphabetically by name, title, or first column
    return filtered.sort((a, b) => {
      // Try to sort by 'name' field first
      if (a.name && b.name) {
        return String(a.name).localeCompare(String(b.name), undefined, { sensitivity: 'base' });
      }
      // Try to sort by 'title' field
      if (a.title && b.title) {
        return String(a.title).localeCompare(String(b.title), undefined, { sensitivity: 'base' });
      }
      // Sort by first column if available
      if (finalColumns && finalColumns.length > 0) {
        const firstColKey = finalColumns[0].key;
        const aVal = a[firstColKey];
        const bVal = b[firstColKey];
        if (aVal && bVal && !React.isValidElement(aVal) && !React.isValidElement(bVal)) {
          return String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
        }
      }
      return 0;
    });
  }, [finalData, searchTerm, columnFilters, finalColumns, requiredTags]);

  // Get unique values for filter dropdowns
  const getUniqueValues = (columnKey: string) => {
    const values = new Set<string>();
    (finalData || []).forEach((row) => {
      const value = row[columnKey];
      if (value === null || value === undefined) return;

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (typeof item === "string") {
            values.add(item);
          }
        });
      } else if (typeof value === "string") {
        values.add(value);
      }
    });
    return Array.from(values).sort();
  };

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startPage = Math.max(1, currentPage - 5);
  const endPage = Math.min(totalPages, currentPage + 5);

  // Reset to page 1 if current page is greater than total pages
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages && currentPage !== 1) {
      handlePageChange(1);
    }
  }, [totalPages, currentPage, handlePageChange]);

  // Paginate filtered data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const actualData = filteredData.slice(startIndex, startIndex + pageSize);

    // Always return exactly pageSize number of rows to maintain consistent table height
    const result = [...actualData];
    while (result.length < pageSize) {
      result.push({} as TableRow); // Add empty row
    }

    return result;
  }, [filteredData, currentPage, pageSize]);

  // Calculate actual data count for current page
  const actualDataCount = useMemo(() => {
    if (filteredData.length === 0) return 0;
    const startIndex = (currentPage - 1) * pageSize;
    return Math.min(filteredData.length - startIndex, pageSize);
  }, [filteredData.length, currentPage, pageSize]);

  // Helper function to render cell content
  const renderCellContent = (
    col: TableColumn,
    value: React.ReactNode,
    row?: TableRow,
    rowIndex?: number
  ) => {
    if (col.key.toLowerCase() === "actions" && hasActions && row) {
      return (
        <div className="flex items-center gap-2">
          {onEdit && (
            <button
              onClick={() => {
                onEdit(row);
              }}
              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title={t('platform.common.edit')}
            >
              <IconProvider
                iconstring="fa-pen-to-square"
                size={14}
                color="currentColor"
              />
            </button>
          )}
          {onDuplicate && (
            <button
              onClick={() => onDuplicate(row)}
              className="p-1.5 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
              title={t('platform.common.duplicate')}
            >
              <IconProvider
                iconstring="fa-copy"
                size={14}
                color="currentColor"
              />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(row)}
              className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title={t('platform.common.delete')}
            >
              <IconProvider
                iconstring="fa-trash"
                size={14}
                color="currentColor"
              />
            </button>
          )}
        </div>
      );
    }
    // Check if this is a tags column (case-insensitive)
    if (col.key.toLowerCase() === "tags") {
      // Handle different tag formats
      if (Array.isArray(value)) {
        // If value is an array of strings
        return (
          <div className="flex flex-wrap gap-1">
            {value.map((tag, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {typeof tag === "string" ? tag : String(tag)}
              </Badge>
            ))}
          </div>
        );
      } else if (React.isValidElement(value)) {
        // If value is already a React element (like existing badge)
        return value;
      } else if (typeof value === "string") {
        // If value is a string, split by comma and create separate badges
        const tags = value
          .split(",")
          .map((tag) => tag.trim())
          .filter((tag) => tag.length > 0);
        return (
          <div className="flex flex-wrap gap-1">
            {tags.map((tag, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        );
      }
    }
    // Handle editable text cells
    if (col.editable === "text" && typeof value === "string" && rowIndex !== undefined) {
      const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.columnKey === col.key;
      
      if (isEditing) {
        return (
          <div className="relative w-full">
            <Input
              value={editingCell.value}
              onChange={(e) => setEditingCell(prev => prev ? { ...prev, value: e.target.value } : null)}
              onKeyDown={(e) => handleCellKeyDown(e, rowIndex, col.key)}
              onBlur={() => handleCellSave(rowIndex, col.key, editingCell.value)}
              className="h-6 text-sm px-2 py-1 pr-12 w-full"
              autoFocus
            />
            <div className="absolute right-0 top-0 flex items-center h-6">
              <button
                onClick={() => handleCellSave(rowIndex, col.key, editingCell.value)}
                className="p-1 text-green-600 hover:text-green-700"
                title={t('platform.common.save')}
              >
                <IconProvider iconstring="fa-check" size={12} color="currentColor" />
              </button>
              <button
                onClick={handleCellCancel}
                className="p-1 text-red-600 hover:text-red-700"
                title={t('platform.common.cancel')}
              >
                <IconProvider iconstring="fa-times" size={12} color="currentColor" />
              </button>
            </div>
          </div>
        );
      } else {
        return (
          <div 
            className="block truncate cursor-pointer hover:bg-gray-100 px-1 py-0.5 rounded-sm"
            onClick={() => handleCellEdit(rowIndex, col.key, value)}
            title={t('platform.cards.widgets.click_to_edit')}
          >
            {value}
          </div>
        );
      }
    }

    // For non-tag columns, wrap string values in a span for proper truncation
    if (typeof value === "string") {
      return <span className="block truncate">{value}</span>;
    }

    // Default rendering for non-tag columns
    return value;
  };

  const handleColumnFilterChange = (columnKey: string, value: string) => {
    setColumnFilters((prev) => ({
      ...prev,
      [columnKey]: value,
    }));
    // Reset to first page when filtering
    handlePageChange(1);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setColumnFilters({});
    handlePageChange(1);
  };

  const hasActiveFilters =
    searchTerm || Object.values(columnFilters).some((v) => v);

  // Determine if we should show the Add New button
  const shouldShowAddNew = showAddNew !== undefined ? showAddNew : !!onAddNew;

  // Handle inline editing
  const handleCellEdit = (rowIndex: number, columnKey: string, currentValue: string) => {
    setEditingCell({
      rowIndex,
      columnKey,
      value: currentValue,
    });
  };

  const handleCellSave = (rowIndex: number, columnKey: string, newValue: string) => {
    if (paginatedData[rowIndex]) {
      // Get the current value from the data
      const currentValue = paginatedData[rowIndex][columnKey];
      
      // Only update if the value has actually changed
      if (currentValue !== newValue) {
        // Check if this is a source text edit and if there are existing translations
        if (columnKey === 'source_text' && dataSource === 'strings') {
          const rowData = paginatedData[rowIndex];
          const translations = rowData.translations as any[] | undefined;
          
          if (translations && translations.length > 0) {
            // Show translation warning dialog
            setTranslationWarning({
              open: true,
              rowIndex,
              columnKey,
              newValue,
              translationCount: translations.length,
            });
            return; // Don't proceed with update yet
          }
        }
        
        // Proceed with normal update
        if (onCellUpdate) {
          /*  console.log('📤 Calling onCellUpdate with:', {
            row: paginatedData[rowIndex],
            columnKey,
            newValue
          }); */
          onCellUpdate(paginatedData[rowIndex], columnKey, newValue);
        } else {
          console.log('❌ onCellUpdate not available');
        }
      } else {
        console.log('📝 No change detected, skipping update');
      }
    } else {
      console.log('❌ No data at rowIndex');
    }
    setEditingCell(null);
  };

  const handleCellCancel = () => {
    setEditingCell(null);
  };

  // Handle translation warning dialog actions
  const handleTranslationWarningConfirm = (action: 'remove' | 'unapprove') => {
    if (!translationWarning) return;
    
    const { rowIndex, columnKey, newValue } = translationWarning;
    const rowData = paginatedData[rowIndex];
    
    if (onCellUpdate) {
      // First update the source text
      onCellUpdate(rowData, columnKey, newValue);
      
      // Then handle translations based on user choice
      const translations = rowData.translations as any[] | undefined;
      if (translations) {
        translations.forEach((translation) => {
          if (action === 'remove') {
            // Remove translation text (set to empty)
            onCellUpdate(rowData, `translations.${translation.language_code}.translated_text`, '');
          } else if (action === 'unapprove') {
            // Unapprove translation (set approved to false)
            onCellUpdate(rowData, `translations.${translation.language_code}.approved`, 'false');
          }
        });
      }
    }
    
    // Close the dialog and clear editing state
    setTranslationWarning(null);
    setEditingCell(null);
  };

  const handleTranslationWarningCancel = () => {
    setTranslationWarning(null);
    // Keep editing state active so user can modify their input
  };

  const handleCellKeyDown = (e: React.KeyboardEvent, rowIndex: number, columnKey: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCellSave(rowIndex, columnKey, editingCell?.value || '');
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCellCancel();
    }
  };

  // Handle row expansion/collapse
  const handleRowToggle = (rowIndex: number, rowData: TableRow) => {
    const rowId = rowData.id as string || `row-${rowIndex}`;
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  return (
    <CardBase
      className="flex flex-col w-full h-full"
      shadowType={shadowType}
      shadowColor={shadowColor}
      backgroundColor={backgroundColor}
    >
      {showTitle && (
        <div className="p-4 pb-2 border-b border-gray-300">
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-gray-900">{title}</span>
            {shouldShowAddNew && onAddNew && (
              <button
                onClick={onAddNew}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                {addNewButtonText ||
                  `Add New ${
                    DATA_SOURCES[dataSource]?.title || dataSource
                  } Item`}
              </button>
            )}
          </div>
        </div>
      )}
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
        </div>
      )}
      {/* Search and Filters */}
      {(showSearch || showFilters) && (
        <div className="p-4 border-b border-gray-200 space-y-3">
          {/* Search Bar */}
          {showSearch && (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input
                  type="text"
                  placeholder={t('platform.cards.widgets.search_across_all_columns')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    handlePageChange(1);
                  }}
                  className="pl-8"
                />
                <svg
                  className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          )}

          {/* Results count */}
          <div className="text-xs text-gray-500">
            Showing {actualDataCount} of {filteredData.length} results
            {finalData &&
              finalData.length !== filteredData.length &&
              ` (filtered from ${finalData.length} total)`}
          </div>
        </div>
      )}

      <div className="overflow-x-auto flex-1 relative">
        <table className="min-w-full border-separate border-spacing-0">
          <thead>
            <tr className="bg-white">
              {/* Expand/collapse header column */}
              {isExpandable && (
                <th className="px-2 py-3 font-semibold text-gray-900 text-sm border-b border-gray-300 w-8">
                  {/* Empty header for expand/collapse column */}
                </th>
              )}
              {(columnsWithActions || []).map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 font-semibold text-gray-900 text-sm border-b border-gray-300 whitespace-nowrap ${
                    col.className || ""
                  } ${col.align === "center" ? "text-center" : ""}`}
                  style={{ textAlign: col.align || "left" }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, i) => {
              // Check if this is an empty row (no data)
              const isEmptyRow = Object.keys(row).length === 0;
              const rowId = row.id as string || `row-${i}`;
              const isExpanded = expandedRows.has(rowId);

              return (
                <React.Fragment key={i}>
                  <tr
                    className={`bg-white ${isEmptyRow ? "bg-white" : "bg-white"} h-14`}
                  >
                    {/* Expand/collapse indicator column */}
                    {isExpandable && (
                      <td className="py-2 text-gray-800 text-sm border-b border-gray-200 w-8">
                        {!isEmptyRow ? (
                          <div 
                            className="flex items-center justify-center cursor-pointer hover:bg-gray-100 rounded p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowToggle(i, row);
                            }}
                          >
                            <IconProvider
                              iconstring={isExpanded ? "fa-chevron-down" : "fa-chevron-right"}
                              size={12}
                              color="currentColor"
                            />
                          </div>
                        ) : (
                          <div className="ml-2">
                            {/* Empty space to maintain consistent spacing */}
                          </div>
                        )}
                      </td>
                    )}
                    {(columnsWithActions || []).map((col) => (
                      <td
                        key={col.key}
                        className={`px-2 py-2 text-gray-800 text-sm border-b border-gray-200 max-w-[180px] ${
                          col.key.toLowerCase() === "tags" ? "" : "truncate"
                        } ${col.className || ""} ${
                          col.align === "center" ? "text-center" : ""
                        }`}
                        style={{ textAlign: col.align || "left" }}
                      >
                        <div
                          className={
                            col.align === "center"
                              ? "flex justify-center items-center gap-2"
                              : col.key.toLowerCase() === "tags"
                              ? ""
                              : "truncate"
                          }
                          title={
                            typeof row[col.key] === "string"
                              ? (row[col.key] as string)
                              : undefined
                          }
                        >
                          {isEmptyRow ? (
                            <span>&nbsp;</span>
                          ) : (
                            renderCellContent(col, row[col.key], row, i)
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                  {/* Expandable content row */}
                  {isExpandable && isExpanded && !isEmptyRow && (
                    <tr>
                      <td colSpan={(columnsWithActions?.length || 0) + (isExpandable ? 1 : 0)} className="p-0">
                        <ExpandableTableContent 
                          rowData={row} 
                          dataSource={dataSource} 
                          onCellUpdate={onCellUpdate}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {/* Transparent overlay when no data source data is available */}
        {!hasDataSourceData && !isLoading && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
            <div className="text-center">
              <div className="text-gray-500 text-lg font-medium mb-2">
                Connect a Datasource
              </div>
              <div className="text-gray-400 text-sm">
                No data available from the selected data source
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 text-xs text-gray-600">
          <div>
            <button
              className="px-2 py-1 disabled:text-gray-300"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              &larr; Prev {pageSize}
            </button>
          </div>
          <div className="flex items-center gap-1">
            {Array.from(
              { length: endPage - startPage + 1 },
              (_, idx) => startPage + idx
            ).map((p) => (
              <button
                key={p}
                className={`px-2 py-1 rounded ${
                  p === currentPage
                    ? "text-purple-600 font-bold border-b-2 border-purple-500"
                    : "hover:text-purple-600"
                }`}
                onClick={() => handlePageChange(p)}
              >
                {p}
              </button>
            ))}
          </div>
          <div>
            <button
              className="px-2 py-1 disabled:text-gray-300"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next {pageSize} &rarr;
            </button>
          </div>
        </div>
      )}
      
      {/* Translation Warning Dialog */}
      {translationWarning && (
        <TranslationWarningDialog
          open={translationWarning.open}
          onOpenChange={(open) => {
            if (!open) {
              setTranslationWarning(null);
            }
          }}
          onConfirm={handleTranslationWarningConfirm}
          onCancel={handleTranslationWarningCancel}
          translationCount={translationWarning.translationCount}
        />
      )}
    </CardBase>
  );
};

export default TableWidget;
